<script setup>
import { useDialogPluginComponent } from "quasar";
import { fetchWithHeaders } from "@/utils";
import { toISOString, toDate, toDuration, toDjangoDate, dayjs } from "@/filters";
import { ref, reactive } from "vue";

const props = defineProps({
    session: {
        type: Object,
        required: true,
    },
});

defineEmits([
    // REQUIRED; need to specify some events that your
    // component will emit through useDialogPluginComponent()
    ...useDialogPluginComponent.emits,
]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
// dialogRef      - Vue ref to be applied to QDialog
// onDialogHide   - Function to be used as handler for @hide on QDialog
// onDialogOK     - Function to call to settle dialog with "ok" outcome
//                    example: onDialogOK() - no payload
//                    example: onDialogOK({ /*...*/ }) - with payload
// onDialogCancel - Function to call to settle dialog with "cancel" outcome
const creating = ref(false);

const proxyDate = ref(null);
const start = ref(props.session.open_date);
const end = ref(props.session.close_date);

function limitToSessionDateRange(date) {
    return date >= props.session.open_date.replace(/-/g, "/") && date <= props.session.close_date.replace(/-/g, "/");
}

function saveRangeDates() {
    if (!proxyDate.value) return;
    if (typeof proxyDate.value === "string") {
        start.value = proxyDate.value;
        end.value = proxyDate.value;
    } else {
        start.value = proxyDate.value.from;
        end.value = proxyDate.value.to;
    }
}

const SUNDAY = 0;
const SATURDAY = 6;

async function onOKClick() {
    // generate all slots and send to api, ignore week-ends
    creating.value = true;
    const generatedSlots = [];
    let day = dayjs(start.value);
    const endDayjs = dayjs(end.value);
    while (day <= endDayjs) {
        const dayOfWeek = day.day();
        const isWeekend = dayOfWeek === SUNDAY || dayOfWeek === SATURDAY;
        if (!isWeekend) {
            slots.value.forEach(slot => {
                const copy = {};
                Object.assign(copy, slot);
                console.log(copy.start);
                copy.start = toISOString(`${toDjangoDate(day)} ${copy.start}`);
                console.log(copy.start);
                copy.end = toISOString(`${toDjangoDate(day)} ${copy.end}`);
                copy.session = props.session.code;
                generatedSlots.push(copy);
            });
        }
        day = day.add(1, "day");
    }
    const url = `${window._env_.API_ENDPOINT}/slots/`;
    await fetchWithHeaders(url, {
        method: "POST",
        body: JSON.stringify(generatedSlots),
    });
    creating.value = false;
    // on OK, it is REQUIRED to
    // call onDialogOK (with optional payload)
    // update with
    onDialogOK();
}

// Compteur pour générer des IDs uniques
let slotIdCounter = 0;
function generateSlotId() {
    return ++slotIdCounter;
}

// HARDCODED initial config
const slots = ref([
    {
        id: generateSlotId(),
        start: "08:30",
        end: "10:00",
        room: props.session.default_room,
        seats: props.session.default_seats,
    },
    {
        id: generateSlotId(),
        start: "10:30",
        end: "12:00",
        room: props.session.default_room,
        seats: props.session.default_seats,
    },
    {
        id: generateSlotId(),
        start: "13:30",
        end: "15:00",
        room: props.session.default_room,
        seats: props.session.default_seats,
    },
]);
const initialPagination = {
    page: 1,
    rowsPerPage: 0,
    sortBy: "id",
    descending: false,
};

const columns = [
    { name: "start", label: "Début", field: "start", align: "left" },
    { name: "end", label: "Fin", field: "end", align: "left" },
    { name: "duration", label: "Durée", align: "left" },
    { name: "room", label: "Salle", field: "room", align: "left" },
    {
        name: "seats",
        label: "Places",
        field: "seats",
        align: "left",
    },
    { name: "action", label: "Action" },
];

const newSlot = reactive({
    start: "",
    end: "",
    room: props.session.default_room,
    seats: props.session.default_seats,
});

function addSlot() {
    // Validation basique : les deux champs doivent être remplis
    if (!newSlot.start || !newSlot.end) return;

    // Validation : end doit être après start
    if (newSlot.end <= newSlot.start) {
        return;
    }

    // Ajouter une copie du slot avec un ID unique
    const slotCopy = JSON.parse(JSON.stringify(newSlot));
    slotCopy.id = generateSlotId();
    slots.value.push(slotCopy);

    // Réinitialiser les champs horaires (garder room et seats)
    newSlot.start = "";
    newSlot.end = "";
}
</script>

<template>
    <q-dialog ref="dialogRef" persistent no-esc-dismiss no-backdrop-dismiss maximized @hide="onDialogHide">
        <q-card class="q-dialog-plugin">
            <q-card-section>
                <div class="text-h6">Génération de créneaux</div>
                <q-field label="Jours à créer (week-end sont ignorés)" stack-label>
                    <template #control>
                        <div class="self-center no-outline" tabindex="0">{{ toDate(start) }} - {{ toDate(end) }}</div>
                    </template>
                    <template #append>
                        <q-btn icon="fa-solid fa-calendar-day" flat color="primary">
                            <q-popup-proxy
                                transition-show="scale"
                                transition-hide="scale"
                                @before-show="proxyDate = { from: start, to: end }"
                            >
                                <q-date v-model="proxyDate" mask="YYYY-MM-DD" range :options="limitToSessionDateRange">
                                    <div class="row items-center justify-end q-gutter-sm">
                                        <q-btn v-close-popup label="Annuler" color="primary" flat />
                                        <q-btn v-close-popup label="OK" color="primary" flat @click="saveRangeDates" />
                                    </div>
                                </q-date>
                            </q-popup-proxy>
                        </q-btn>
                    </template>
                </q-field>
            </q-card-section>
            <q-card-section>
                <q-table
                    title="Jour type"
                    flat
                    row-key="id"
                    :columns="columns"
                    :rows="slots"
                    :pagination="initialPagination"
                    :loading="creating"
                    hide-bottom
                    square
                >
                    <template #body="rowProps">
                        <q-tr :key="rowProps.row.id" :props="rowProps">
                            <q-td>
                                <q-input
                                    v-model="rowProps.row.start"
                                    dense
                                    filled
                                    mask="##:##"
                                    stack-label
                                    placeholder="08:00"
                                    style="max-width: 80px"
                                />
                            </q-td>
                            <q-td>
                                <q-input
                                    v-model="rowProps.row.end"
                                    dense
                                    filled
                                    mask="##:##"
                                    stack-label
                                    placeholder="10:00"
                                    style="max-width: 80px"
                                />
                            </q-td>
                            <q-td>
                                {{
                                    toDuration(
                                        toISOString(`${toDjangoDate(new Date())} ${rowProps.row.start}`),
                                        toISOString(`${toDjangoDate(new Date())} ${rowProps.row.end}`)
                                    )
                                }}
                            </q-td>
                            <q-td>
                                <q-input v-model="rowProps.row.room" dense filled placeholder="Salle" />
                            </q-td>
                            <q-td class="text-right">
                                <q-input
                                    v-model="rowProps.row.seats"
                                    dense
                                    filled
                                    type="number"
                                    min="0"
                                    style="max-width: 100px"
                                />
                            </q-td>
                            <q-td class="text-right"
                                ><q-btn
                                    icon="fa-solid fa-trash"
                                    round
                                    flat
                                    color="negative"
                                    size="sm"
                                    @click="slots.splice(slots.indexOf(rowProps.row), 1)"
                                ></q-btn>
                            </q-td>
                        </q-tr>
                    </template>
                    <template #bottom-row>
                        <q-tr>
                            <q-td>
                                <q-input
                                    v-model="newSlot.start"
                                    dense
                                    mask="##:##"
                                    filled
                                    placeholder="08:00"
                                    style="max-width: 80px"
                                />
                            </q-td>
                            <q-td>
                                <q-input
                                    v-model="newSlot.end"
                                    dense
                                    mask="##:##"
                                    filled
                                    placeholder="10:00"
                                    style="max-width: 80px"
                                />
                            </q-td>
                            <q-td>
                                <div>
                                    {{
                                        toDuration(
                                            toISOString(`${toDjangoDate(new Date())} ${newSlot.start}`),
                                            toISOString(`${toDjangoDate(new Date())} ${newSlot.end}`)
                                        )
                                    }}
                                </div>
                            </q-td>
                            <q-td>
                                <q-input v-model="newSlot.room" dense filled />
                            </q-td>
                            <q-td class="text-right">
                                <q-input
                                    v-model="newSlot.seats"
                                    dense
                                    filled
                                    type="number"
                                    min="0"
                                    style="max-width: 100px"
                                />
                            </q-td>
                            <q-td class="text-right">
                                <q-btn
                                    icon="fa-solid fa-plus"
                                    label="ajouter"
                                    unelevated
                                    color="primary"
                                    :disable="!newSlot.end || !newSlot.start || newSlot.end <= newSlot.start"
                                    @click="addSlot"
                                ></q-btn>
                            </q-td>
                        </q-tr>
                    </template>
                </q-table>
            </q-card-section>

            <q-card-actions v-if="!creating" align="right">
                <q-btn label="Annuler" @click="onDialogCancel" />
                <q-btn color="warning" label="Créer tous les créneaux" @click="onOKClick" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>
